import React from 'react';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import NotificationsNoneIcon from '@material-ui/icons/NotificationsNone';
import { RouterLink } from '@components/RouterLink/RouterLink';
import { track } from '@amplitude/analytics-browser';
import { BaseEventTrackingWrapper } from '@components/BaseEventTrackingWrapper/BaseEventTrackingWrapper';
import { TrackableEventType } from '@components/BaseEventTrackingWrapper/BaseTrackableEvents.types';
import ColumnHeaderProps from '@components/SchedulePage/types/ColumnHeaderProps';
import { BlockReleaseState } from '@data/blockReleases/types/BlockReleaseState';
import { BlockReleaseApiResponse } from '@data/blockReleases/types/BlockRelease.types';
import { useSchedulePageContext } from '@components/SchedulePage/SchedulePageContext/SchedulePageContext';

const useStyles = makeStyles((theme) =>
	createStyles({
		root: {
			'&:link, &:visited, &:hover, &:active': {
				color: theme.palette.gold.main,
				textDecoration: 'underline',
				fontSize: '0.875rem',
			},
		},
	}),
);

type BlockReleaseRecommendationLinkProps = ColumnHeaderProps;
const RELEASE_LINK_EVENTS_TO_TRACK = [TrackableEventType.OnClick, TrackableEventType.OnKeyDown];
const DATA_FIELD = 'scheduling-blockColumn-blockReleaseRecommendationLink';

const isSameDate = (date1: string, date2: string) => {
	const date1String = date1.split(' ')[0];
	const date2String = date2.split(' ')[0];
	return date1String === date2String;
};

export const BlockReleaseRecommendationLink: React.FC<BlockReleaseRecommendationLinkProps> = ({
	blockInfo,
}) => {
	const styles = useStyles();
	const { releases } = useSchedulePageContext();

	const handleTrackingEvent = React.useCallback((event: React.SyntheticEvent<HTMLElement>) => {
		const eventType = event.type;
		const eventName = `link-${eventType}`;
		track(eventName, { fieldName: DATA_FIELD, eventType: eventType });
	}, []);

	if (!blockInfo?.blocks?.length || !releases.length) {
		return null;
	}

	const recommendedBlocks =
		blockInfo.blocks.filter(
			(block) => (block.release?.state === BlockReleaseState.RECOMMENDED_RELEASE),
		);

	const recommendedBlocksReleases = recommendedBlocks.reduce<BlockReleaseApiResponse[]>(
		(acc, block) => {
			const matchingReleases =
				releases?.filter(
					(release) =>
						(release.originalReleases ? release.originalReleases.some((releaseBlock) => releaseBlock.slotId === block.id) : release.slotId === block.id) &&
						isSameDate(release.startDateTime, block.start),
				);

			return [...acc, ...matchingReleases];
		},
		[],
	);

	let recommendedBlockReleasesLength = recommendedBlocksReleases?.length;
	if (recommendedBlocksReleases[0]?.originalReleases?.length) {
		recommendedBlockReleasesLength = recommendedBlocksReleases[0]?.originalReleases?.length;
	}

	const shouldShowLink = recommendedBlocks?.length > 0 && recommendedBlocks.length === recommendedBlockReleasesLength;

	if (!shouldShowLink) {
		return null;
	}

	return (
		<BaseEventTrackingWrapper
			handleEvent={handleTrackingEvent}
			dataField={DATA_FIELD}
			eventsToTrack={RELEASE_LINK_EVENTS_TO_TRACK}
		>
			<RouterLink
				classes={styles}
				to={`/blockrelease/recommendation/${recommendedBlocksReleases[0].id}`}
				prefixIcon={NotificationsNoneIcon}
			>
				Release Block Recommended
			</RouterLink>
		</BaseEventTrackingWrapper>
	);
};
